import { addYears, format } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { fetchWithWatch } from '../../hooks/fetchWithWatch';
import { getCookie, getUrlVersion, setLoading } from '../../pages/commons/functions';

import CartesianChart from '@cloudscape-design/chart-components/cartesian-chart';
import Highcharts from "highcharts";
import 'highcharts/highcharts-more';


interface Movement {
    idMovement: number;
    dateTransaction: string;
    balance: number;
    charge: number;
    payment: number;
}

interface DailySummary {
    date: string;
    charges: number;
    payments: number;
    balance: number;
    _maxId?: number; // Optional temporary field
}

interface DailyBalancesProps {
    idUser?: number;
    from?: string;
    to?: string;
}


// Use Highcharts native series type definition matching bubble charts
export const DailyBalances: React.FC<DailyBalancesProps> = ({
    idUser = 0,
    to = format(new Date(), "dd-MM-yyyy"),
    from = format(addYears(new Date(), -1), "dd-MM-yyyy")
}) => {
    const [movements, setMovements] = useState<any[]>([]);

    const chartSeries = React.useMemo(() => {
        const chargesSeriesData: { x: number; y: number; size: number }[] = [];
        const paymentsSeriesData: { x: number; y: number; size: number }[] = [];

        (movements || []).forEach((item: any) => {
            const dateStr = item.date; // "DD-MM-YYYY"
            if (!dateStr) return;

            const [day, month, year] = dateStr.split("-").map(Number);
            const timestamp = Date.UTC(year, month - 1, day);

            if (item.charges && item.charges !== 0) {
                chargesSeriesData.push({
                    x: timestamp,
                    y: item.balance,
                    size: Math.abs(item.charges)
                });
            }

            if (item.payments && item.payments !== 0) {
                paymentsSeriesData.push({
                    x: timestamp,
                    y: item.balance,
                    size: Math.abs(item.payments)
                });
            }
        });

        return [
            {
                name: "Cargos",
                type: "bubble",
                color: "#ef4444",
                data: chargesSeriesData,
            },
            {
                name: "Abonos",
                type: "bubble",
                color: "#3b82f6",
                data: paymentsSeriesData,
            },
        ] as any[];
    }, [movements]);

    useEffect(() => {
        handleGetDailyBalances();
    }, [idUser, from, to]);

    const handleGetDailyBalances = async () => {
        if (idUser > 0) {
            try {
                setLoading(true);
                const response = await fetchWithWatch(`${getUrlVersion()}/routing/v1/`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'x-routing': '/private/admin/movements/list',
                        'JWT': getCookie("JWT"),
                    },
                    body: JSON.stringify({
                        idUser: idUser,
                        limit: -1,
                        orderBy: "dateTransaction",
                        order: "desc",
                        "dateStart": from,
                        "dateEnd": to,
                        orderBy2: "idMovement",
                        order2: "desc",
                        columns: "idMovement, FORMAT(dateTransaction,'yyyy-MM-dd') as dateTransaction, balance, charge, payment"
                    }),
                });

                if (!response.ok) {
                    throw new Error(`HTTP error: ${response.status}`);
                }
                const dataWeb = await response.json();
                if (dataWeb.code === 0) {
                    setMovements(processMovements(dataWeb.movements));
                }
            } catch (error: any) {
                //addNotification(`Error de red y/o comunicaciones: ${error.message}`, "error", setChartSeries);
            } finally {
                setLoading(false);
            }
        }
    };

    const processMovements = (data: Movement[]): Omit<DailySummary, '_maxId'>[] => {
        const grouped = data.reduce<Record<string, DailySummary>>((acc, curr) => {
            const date = curr.dateTransaction;

            if (!acc[date]) {
                acc[date] = {
                    date: date,
                    charges: 0,
                    payments: 0,
                    balance: curr.balance,
                    _maxId: curr.idMovement,
                };
            }

            acc[date].charges += curr.charge || 0;
            acc[date].payments += curr.payment || 0;

            if (curr.idMovement > (acc[date]._maxId || 0)) {
                acc[date]._maxId = curr.idMovement;
                acc[date].balance = curr.balance;
            }

            return acc;
        }, {});

        // Explicitly type the destructured parameter in map
        return Object.values(grouped).map(({ _maxId, ...rest }) => rest);
    };
    return (
        chartSeries.length > 0 && !!movements && movements.length > 0 ? (
            <CartesianChart
                highcharts={Highcharts}
                ariaLabel="Balance bubble chart"
                chartHeight={400}
                series={chartSeries}
                sizeAxis={{
                    title: "Monto",
                    valueFormatter: e => `$${e.toLocaleString()}`
                }}
                xAxis={{
                    type: "datetime",
                    title: "Fecha",
                    valueFormatter: e => new Date(e)
                        .toLocaleDateString("es-ES", {
                            month: "short",
                            year: "numeric",
                            timeZone: "UTC"
                        })
                        .split(" ")
                        .join("\n")
                        .toUpperCase()
                }}
                yAxis={{
                    title: "Balance",
                    valueFormatter: function (e) {
                        return `$${e.toLocaleString()}`;
                    }
                }}
            />
        ) : null
    );
};

export default DailyBalances;